import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { PostgresLayerServiceConfigurationDc } from "@evergis/api";
import { RangeValue } from "@evergis/ui";

import { castScaleToResolution } from "../../../../evergis/core/scale";
import { useLayerUpdate } from "../../../../hooks/layer/useLayerUpdate";
import { useNotification } from "../../../../hooks/notifications/useNotification";
import { useTrigger } from "../../../../hooks/useTrigger";
import { useDialogInfo } from "../../../../hooks/useDialogs";
import { layer as layerApi } from "../../../../resources/layers";

import { LayerInfo } from "../../../../interfaces/resourceInfo";
import { ProxyServiceType } from "../../AddProxyService/components/types";
import { useUpdateProxyService } from "../../EditResource/hooks/useUpdateProxyService";

export const useScaleRestrictionUpdate = (layer: LayerInfo, scale: RangeValue) => {
  const { t } = useTranslation("dialogs");
  const { data: layerInfo } = layerApi.use({ input: layer.name, waitInput: true });
  const { closeDialog } = useDialogInfo(true);
  const [minResolution, maxResolution] = castScaleToResolution(scale);
  const [updateLayer, { updating, error, updatedLayer }] = useLayerUpdate(layer);
  const { updateProxyLayer } = useUpdateProxyService({
    name: layer.name,
    meta: {
      alias: layer.alias,
      description: layer.description,
      preview: {
        src: layer.preview,
      },
      copyrightText: layer.copyrightText,
      name: layer.name,
    },
    serviceType: layer.proxy.sourceType as ProxyServiceType,
    sourceUrl: layer.proxy.sourceUrl,
    layers: layer.proxy.layers,
    minResolution,
    maxResolution,
  });

  const updateScaleRestriction = useCallback(() => {
    if (!layer.style) {
      return closeDialog();
    }

    if (layer.type === "ProxyService") {
      return updateProxyLayer();
    }

    const style = {
      ...(layerInfo as PostgresLayerServiceConfigurationDc).style,
      minResolution,
      maxResolution,
    };

    return updateLayer({ style });
  }, [layer, closeDialog, updateLayer, layerInfo, updateProxyLayer, minResolution, maxResolution]);

  useNotification(!updating && error, {
    error: true,
    title: t("scaleRestriction.scaleRestrictionError"),
  });

  useNotification(!updating && updatedLayer, {
    success: true,
    title: t("scaleRestriction.scaleRestrictionSuccess"),
  });

  useTrigger(!updating && updatedLayer, closeDialog);

  return updateScaleRestriction;
};
